import { useState, useEffect } from 'react';
import { type MoodEntry, type MoodLevel, MOOD_CONFIG, formatDate, saveEntry } from '../api/mockData';
import { MoodSelector } from './MoodSelector';
import { NoteInput } from './NoteInput';

interface TodayViewProps {
  entries: Record<string, MoodEntry>;
  onEntriesChange: (entries: Record<string, MoodEntry>) => void;
}

export function TodayView({ entries, onEntriesChange }: TodayViewProps) {
  const today = formatDate(new Date());
  const todayEntry = entries[today];

  const [mood, setMood] = useState<MoodLevel | undefined>(todayEntry?.mood);
  const [note, setNote] = useState(todayEntry?.note || '');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setMood(todayEntry?.mood);
    setNote(todayEntry?.note || '');
  }, [todayEntry]);

  const handleSave = (selectedMood: MoodLevel) => {
    setMood(selectedMood);
    const updated = saveEntry({ date: today, mood: selectedMood, note: note.trim() || undefined });
    onEntriesChange(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleNoteBlur = () => {
    if (mood) {
      const updated = saveEntry({ date: today, mood, note: note.trim() || undefined });
      onEntriesChange(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const dateObj = new Date();
  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
  const dateStr = dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const moodConfig = mood ? MOOD_CONFIG[mood] : null;

  return (
    <div style={{
      padding: '24px 20px',
      maxWidth: '480px',
      margin: '0 auto',
    }}>
      {/* Date display */}
      <div className="animate-fade-up" style={{
        textAlign: 'center',
        marginBottom: '32px',
      }}>
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.85rem',
          fontWeight: 500,
          color: 'var(--journal-ink-faint)',
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          margin: '0 0 4px',
        }}>
          {dayName}
        </p>
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '2rem',
          fontWeight: 800,
          color: 'var(--journal-ink)',
          margin: 0,
          lineHeight: 1.1,
        }}>
          {dateStr}
        </h2>
      </div>

      {/* Current mood hero */}
      {moodConfig ? (
        <div
          className="animate-fade-up stagger-1"
          style={{
            textAlign: 'center',
            padding: '28px 20px',
            marginBottom: '28px',
            borderRadius: '20px',
            background: `linear-gradient(135deg, ${moodConfig.bgColor}, white)`,
            border: `1px solid ${moodConfig.color}44`,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <span style={{
            fontSize: '4rem',
            display: 'block',
            marginBottom: '8px',
            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))',
          }}>
            {moodConfig.emoji}
          </span>
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.2rem',
            fontWeight: 600,
            color: moodConfig.deepColor,
          }}>
            Feeling {moodConfig.label}
          </span>
          {todayEntry?.note && (
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.9rem',
              color: 'var(--journal-ink-light)',
              marginTop: '10px',
              fontStyle: 'italic',
              lineHeight: 1.5,
            }}>
              "{todayEntry.note}"
            </p>
          )}
        </div>
      ) : (
        <div
          className="animate-fade-up stagger-1"
          style={{
            textAlign: 'center',
            padding: '32px 20px',
            marginBottom: '28px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, var(--journal-bg-warm), white)',
            border: '1px dashed var(--journal-line)',
          }}
        >
          <span style={{
            fontSize: '2.5rem',
            display: 'block',
            marginBottom: '10px',
            opacity: 0.4,
          }}>
            📔
          </span>
          <p style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'var(--journal-ink-light)',
            margin: 0,
          }}>
            How are you feeling today?
          </p>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.8rem',
            color: 'var(--journal-ink-faint)',
            margin: '6px 0 0',
          }}>
            Tap a mood to begin
          </p>
        </div>
      )}

      {/* Section label */}
      <p className="animate-fade-up stagger-2" style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: 'var(--journal-ink-light)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: '10px',
        paddingLeft: '4px',
      }}>
        {moodConfig ? 'Change mood' : 'Select your mood'}
      </p>

      {/* Mood selector */}
      <div style={{ marginBottom: '24px' }}>
        <MoodSelector selected={mood} onSelect={handleSave} />
      </div>

      {/* Note input */}
      <p className="animate-fade-up stagger-4" style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: 'var(--journal-ink-light)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: '10px',
        paddingLeft: '4px',
      }}>
        Add a note
      </p>
      <div onBlur={handleNoteBlur}>
        <NoteInput value={note} onChange={setNote} />
      </div>

      {/* Saved confirmation */}
      <div style={{
        textAlign: 'center',
        marginTop: '16px',
        height: '24px',
      }}>
        {saved && (
          <span
            className="animate-fade-up"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: 'var(--mood-great-deep)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            ✓ Saved
          </span>
        )}
      </div>

      {/* Recent entries */}
      <div style={{ marginTop: '32px' }}>
        <p className="animate-fade-up" style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'var(--journal-ink-light)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: '12px',
          paddingLeft: '4px',
        }}>
          Recent entries
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {getRecentEntries(entries).map((entry, i) => {
            const config = MOOD_CONFIG[entry.mood];
            const d = new Date(entry.date + 'T12:00:00');
            const label = entry.date === today
              ? 'Today'
              : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

            return (
              <div
                key={entry.date}
                className={`animate-fade-up stagger-${Math.min(i + 1, 5)}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  borderRadius: '14px',
                  background: 'white',
                  border: '1px solid var(--journal-line)',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{
                  fontSize: '1.6rem',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '10px',
                  background: config.bgColor,
                  flexShrink: 0,
                }}>
                  {config.emoji}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'var(--journal-ink)',
                    margin: 0,
                  }}>
                    {label}
                  </p>
                  {entry.note && (
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.78rem',
                      color: 'var(--journal-ink-light)',
                      margin: '2px 0 0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {entry.note}
                    </p>
                  )}
                </div>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: config.color,
                  flexShrink: 0,
                }} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function getRecentEntries(entries: Record<string, MoodEntry>): MoodEntry[] {
  return Object.values(entries)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 5);
}
